import React, { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import isEqual from 'lodash/isEqual';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import type { DocNode as ADF } from '@atlaskit/adf-schema';
import { getEmptyADF } from '@atlaskit/adf-utils/empty-adf';
import { ModalEntryPointContainer } from '@atlassian/jira-entry-point-modal-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagsService } from '@atlassian/jira-flags';
import {
	fireUIAnalytics,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { asyncFlagWithCommentModal } from '@atlassian/jira-software-add-comment-on-flag-action/entrypoint.tsx';
import type { CommentOnFlaggingUnflaggingSafeEntryPointProps } from '@atlassian/jira-software-add-comment-on-flag-action/src/ui/types.tsx';
import { useFlagIssueModal } from '../controllers/flag-with-comment/index.tsx';
import { saveFlagWithComment } from '../services/save-flag-with-comment/index.tsx';
import { messages } from './messages.tsx';

const entryPointParams = {};
const FlagIssueWithCommentModalBase: FC = () => {
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncFlagWithCommentModal,
		entryPointParams,
	);

	const [initialized, setInitialized] = useState(false);
	const [{ flagAction, issues, forcePreload, callback, analyticsArgs }, { close }] =
		useFlagIssueModal();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagsService();

	useEffect(() => {
		if (forcePreload && !initialized) {
			setInitialized(true);
			entryPointActions.preload();
		}
	}, [entryPointActions, forcePreload, initialized]);

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (flagAction !== undefined) {
			entryPointActions.load();
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
			if (!initialized) {
				setInitialized(true);
			}
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
		} else if (initialized) {
			entryPointActions.unload();
		}
	}, [flagAction, initialized, entryPointActions]);

	const issueIds = useMemo(() => issues.map(({ issueId }) => issueId), [issues]);

	// Not using issueIds from modal because it is number[] where they should be IssueId[]
	const handleSaveWithComment = useCallback(
		async (_: number[], flag: boolean, comment: ADF) => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			});

			const extendedAnalyticsArgs = {
				hasComment: !isEqual(comment, getEmptyADF()),
				...analyticsArgs,
			};

			fireUIAnalytics(analyticsEvent, 'confirmButton', extendedAnalyticsArgs);

			close();

			try {
				await saveFlagWithComment(issueIds, flag, comment);

				analyticsEvent.update({
					action: 'flagged',
					actionSubject: 'cards',
				});

				fireTrackAnalytics(analyticsEvent, 'cards flagged', {
					...extendedAnalyticsArgs,
					numCards: issueIds.length,
					flagAction: flag ? 'added' : 'removed',
				});

				if (
					extendedAnalyticsArgs.hasComment &&
					fg('jira_task_off_track_experiment_popup_kill_switch')
				) {
					const cardsFlaggedCommentAnalyticsEvent = createAnalyticsEvent({
						actionSubject: 'cards',
						action: 'flagged_comment',
					});

					fireTrackAnalytics(cardsFlaggedCommentAnalyticsEvent, 'cards flagged_comment', {
						...extendedAnalyticsArgs,
						numCards: issueIds.length,
						flagAction: flag ? 'added' : 'removed',
					});
				}

				if (callback) {
					callback({ issueIds, flag, comment });
				}
			} catch (err) {
				const error = err instanceof Error ? err : new Error('Unknown error');

				fireErrorAnalytics({
					error,
					meta: {
						id: 'jira.addCommentOnFlagSaveAction',
						packageName: 'jiraSoftwareAddIssueFlagWithComment',
						teamName: 'deliveroo',
					},
				});

				const addFlagTitle = fg('jira-issue-terminology-refresh-m3')
					? messages.addFlagFailedTitleIssueTermRefresh
					: messages.addFlagFailedTitle;

				const removeFlagTitle = fg('jira-issue-terminology-refresh-m3')
					? messages.removeFlagFailedTitleIssueTermRefresh
					: messages.removeFlagFailedTitle;

				showFlag({
					key: 'flag-issue-with-comment-modal-error',
					type: 'error',
					title: flag ? addFlagTitle : removeFlagTitle,
					description: flag
						? messages.addFlagFailedDescription
						: messages.removeFlagFailedDescription,
					messageId: 'software-add-issue-flag-with-comment.ui.show-flag.error',
					messageType: 'transactional',
				});
			}
		},
		[createAnalyticsEvent, analyticsArgs, close, issueIds, callback, showFlag],
	);

	const handleOnClose = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		});

		fireUIAnalytics(analyticsEvent, 'cancelButton', {
			...analyticsArgs,
		});

		close();
	}, [analyticsArgs, close, createAnalyticsEvent]);

	const modalProps: CommentOnFlaggingUnflaggingSafeEntryPointProps = useMemo(
		() => ({
			flagAction,
			cards: issues.map(({ issueId, issueKey, summary, type }) => ({
				id: +issueId, // Parsing issueId because FlagWithCommentModal expects number. However this is not used when saving as we are using the IssueIds from the state
				key: issueKey,
				summary: summary ?? '',
				type,
			})),
			onClose: handleOnClose,
			onFlagWithComment: handleSaveWithComment,
		}),
		[flagAction, handleOnClose, handleSaveWithComment, issues],
	);

	return (
		<ModalEntryPointContainer
			entryPointActions={entryPointActions}
			entryPointReferenceSubject={entryPointReferenceSubject}
			id="flag-issue-with-comment-modal"
			packageName="jira-software-add-issue-flag-with-comment"
			runtimeProps={modalProps}
		/>
	);
};

export const FlagIssueWithCommentModal: FC = () => (
	<JSErrorBoundary
		id="jira-software-add-issue-flag-with-comment-entrypoint"
		packageName="JiraSoftwareAddIssueFlagWithComment"
		fallback="unmount"
		teamName="deliveroo"
	>
		<FlagIssueWithCommentModalBase />
	</JSErrorBoundary>
);
